<template>
  <div>
    <canvas
      class="absolute top-0 left-0 w-full h-full object-cover rounded-xl p-1"
      style="z-index: -100"
      ref="visualizer"
      :width="width"
      :height="height"
    ></canvas>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue';

export default {
  name: 'AudioStreamPreview',
  props: {
    deviceId: {
      type: String,
      required: true,
    },
    width: {
      type: Number,
      default: 640,
    },
    height: {
      type: Number,
      default: 480,
    },
  },
  setup(props) {
    const visualizer = ref(null);
    const audioStream = ref(null);
    let audioContext;
    let analyser;
    let dataArray;
    let animationFrame;

    const startAudioStream = async (deviceId) => {
      try {
        const constraints = {
          audio: {
            deviceId: deviceId ? { exact: deviceId } : undefined,
          },
        };

        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        audioStream.value = stream;
        return stream; // Return the stream so we can use it later
      } catch (error) {
        console.error('Error accessing microphone:', error);
      }
      return null;
    };

    onMounted(async () => {
      const stream = await startAudioStream(props.deviceId);
      if (!stream) return;

      const canvas = visualizer.value;
      const canvasCtx = canvas.getContext('2d');

      audioContext = new (window.AudioContext || window.webkitAudioContext)();
      analyser = audioContext.createAnalyser();
      const source = audioContext.createMediaStreamSource(stream);
      const gainNode = audioContext.createGain();
      gainNode.gain.setValueAtTime(5, audioContext.currentTime);
      source.connect(gainNode);
      gainNode.connect(analyser);

      analyser.fftSize = 4096;
      analyser.smoothingTimeConstant = 0.85;
      analyser.minDecibels = -120;
      analyser.maxDecibels = -60;

      const bufferLength = analyser.frequencyBinCount;
      dataArray = new Uint8Array(bufferLength);

      function draw() {
        animationFrame = requestAnimationFrame(draw);

        analyser.getByteTimeDomainData(dataArray);

        canvasCtx.fillStyle = '#000';
        canvasCtx.fillRect(0, 0, props.width, props.height);

        canvasCtx.lineWidth = 2;
        canvasCtx.strokeStyle = 'rgb(255, 255, 255)';
        canvasCtx.beginPath();

        const sliceWidth = props.width / bufferLength;
        let x = 0;

        for (let i = 0; i < bufferLength; i++) {
          const v = dataArray[i] / 128.0;
          const y = (v * props.height) / 2;

          if (i === 0) {
            canvasCtx.moveTo(x, y);
          } else {
            canvasCtx.lineTo(x, y);
          }

          x += sliceWidth;
        }

        canvasCtx.lineTo(props.width, props.height / 2);
        canvasCtx.stroke();
      }

      draw();
    });

    onUnmounted(() => {
      cancelAnimationFrame(animationFrame);

      if (audioStream.value) {
        audioStream.value.getTracks().forEach((track) => track.stop());
        audioStream.value = null;
      }

      if (audioContext) {
        if (audioContext.state !== 'closed') {
          audioContext
            .close()
            .then(() => {
              console.log('AudioContext closed');
            })
            .catch((error) => {
              console.error('Error closing AudioContext:', error);
            });
        }
        audioContext = null;
      }
    });

    return { visualizer };
  },
};
</script>
